/**
 * Component: WealthicaPositionsTable
 * 
 * Displays positions in tabs by asset class. Intended for use within
 * `Wealthica2GoldContainer`.
 */
// Wealthica Positions Table Component - Displays positions in tabs by asset class

import React from 'react';
import { formatCurrency } from '@/lib/portfolioCalculations';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ASSET_CLASS_ICONS } from '@/constants/investmentIcons';
import {
  AccountPosition,
  groupPositionsByClass,
  formatAssetClass,
  ASSET_CLASS_PRIORITY
} from '@/providers/wealthica2/utils/account-position-utils';

// Use the shared AccountPosition type from utilities
type Position = AccountPosition;

interface WealthicaPositionsTableProps {
  positions: Position[];
  accountName: string;
  accountCurrency?: string;
  useAlternatingRows?: boolean;
  onPositionClick?: (position: Position) => void;
}

// Simplified asset class display names for tabs - shorter names for UI space efficiency
const SIMPLIFIED_DISPLAY_NAMES: Record<string, string> = {
  "cash": "Cash",
  "fixed_income": "Income",
  "equity": "Equity",
  "alternative": "Alts",
  "multi_asset": "Multi",
  "unclassified": "Other"
};

// Map from Wealthica asset classes to our Canadian 6-category system
const ASSET_CLASS_MAPPING: Record<string, keyof typeof SIMPLIFIED_DISPLAY_NAMES> = {
  "cash": "cash",
  "cash_equivalent": "cash",
  "fixed_income": "fixed_income",
  "equity": "equity",
  "alternative": "alternative",
  "multi_asset": "multi_asset",
  "other": "unclassified"
};


export function WealthicaPositionsTable({ positions, accountName, accountCurrency = 'CAD', useAlternatingRows = false, onPositionClick }: WealthicaPositionsTableProps) {
  // Use the shared groupPositionsByClass utility
  const groupedPositions = groupPositionsByClass(positions);
  
  // Using the shared formatAssetClass utility

  // Calculate totals
  const calculateTotal = (positions: Position[]): number => {
    return positions.reduce((sum, position) => sum + (position.market_value || 0), 0);
  };

  // Sort asset classes by priority
  const sortedAssetClasses = Object.keys(groupedPositions).sort((a, b) => {
    return (ASSET_CLASS_PRIORITY[a] || 99) - (ASSET_CLASS_PRIORITY[b] || 99);
  });

  // If there are no positions, display a message
  if ((positions?.length || 0) === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        <p>No positions found for this account.</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Tabs defaultValue={sortedAssetClasses[0]} className="w-full">
        <TabsList className="mb-3 grid grid-cols-4 gap-2 p-0.5 bg-muted/20 rounded-md">
          {sortedAssetClasses.map(assetClass => {
            // Map to centralized asset class key
            const mappedClass = ASSET_CLASS_MAPPING[assetClass] || "unclassified";
            
            // Get icon from centralized asset class icons
            const Icon = ASSET_CLASS_ICONS[mappedClass];
            
            // Get the simplified display name using mapped class
            const label = SIMPLIFIED_DISPLAY_NAMES[mappedClass] || formatAssetClass(assetClass);
            
            return (
              <TabsTrigger 
                key={assetClass} 
                value={assetClass}
                className="flex justify-center items-center gap-1.5 px-2 py-1.5 rounded-md transition-all hover:bg-muted/50 data-[state=active]:bg-[#222222] data-[state=active]:text-white data-[state=active]:shadow-sm"
              >
                <Icon className="h-3.5 w-3.5" />
                <span>{label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>
        
        {sortedAssetClasses.map(assetClass => (
          <TabsContent key={assetClass} value={assetClass} className="mt-0">
            <div className="rounded-md overflow-hidden border">
              <table className="w-full">
                <thead>
                  <tr className="bg-muted/20 border-b">
                    <th className="h-10 px-4 text-left font-medium text-muted-foreground">Security</th>
                    <th className="h-10 px-4 text-right font-medium text-muted-foreground">Quantity</th>
                    <th className="h-10 px-4 text-right font-medium text-muted-foreground">Market Value</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedPositions[assetClass].map((position, index) => (
                    <tr 
                      key={position._id} 
                      className={`border-b hover:bg-muted/10 ${useAlternatingRows ? (index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50') : ''} ${onPositionClick ? 'cursor-pointer' : ''}`}
                      onClick={() => onPositionClick?.(position)}
                    >
                      <td className="p-3 pl-4">
                        <div className="font-medium">{position.security.name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{position.security.symbol}</div>
                      </td>
                      <td className="p-3 text-right">
                        <div className="font-medium">
                          {position.quantity?.toLocaleString(undefined, { 
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 6 
                          }) || '-'}
                        </div>
                      </td>
                      <td className="p-3 pr-4 text-right">
                        <div className="font-medium">
                          {formatCurrency(position.market_value, position.currency || accountCurrency, { showDecimals: true, locale: 'en-CA' })}
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-muted/10 font-medium">
                    <td className="p-3 pl-4">Total</td>
                    <td className="p-3"></td>
                    <td className="p-3 pr-4 text-right">
                      {formatCurrency(calculateTotal(groupedPositions[assetClass]), accountCurrency, { showDecimals: true, locale: 'en-CA' })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="text-xs text-muted-foreground text-right mt-2 mr-2">
              {formatAssetClass(assetClass)} - {groupedPositions[assetClass].length} positions
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

// Export as default for flexibility
export default WealthicaPositionsTable;
